import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '@sentinel/api-client';
import { StepUpProvider, type StepUpMessages } from '@sentinel/auth-core';

export interface PortalStepUpProviderProps {
  apiClient: ApiClient;
  children?: ReactNode;
}

/**
 * Portal-specific wrapper around `<StepUpProvider>` that pulls translation
 * strings from the `step-up` i18n namespace.
 */
export function PortalStepUpProvider({ apiClient, children }: PortalStepUpProviderProps) {
  const { t } = useTranslation('step-up');
  const messages = useMemo<StepUpMessages>(
    () => ({
      title: t('title'),
      description: t('description'),
      passwordLabel: t('password_label'),
      totpLabel: t('totp_label'),
      submit: t('submit'),
      cancel: t('cancel'),
      invalidPassword: t('invalid_password'),
      genericError: t('generic_error'),
      descriptionChoose: t('description_choose'),
      methodGroupLabel: t('method_group_label'),
      methodPasswordLabel: t('method_password'),
      methodTotpLabel: t('method_totp'),
      methodWebauthnLabel: t('method_webauthn'),
      webauthnIntro: t('webauthn_intro'),
      webauthnStart: t('webauthn_start'),
      webauthnVerifying: t('webauthn_verifying'),
      webauthnFailed: t('webauthn_failed'),
      webauthnNoCredentials: t('webauthn_no_credentials'),
    }),
    [t],
  );
  return (
    <StepUpProvider apiClient={apiClient} messages={messages}>
      {children}
    </StepUpProvider>
  );
}
